/**
 * The figures under the plan, one set of rules for Dag, Uke and Måned (review 2026-10-27): what a day counts, how
 * the days add up, when a figure is orange, how over or under the target is said, and what is missing. The three
 * sections (DayFigures, WeekFigures, MonthFigures) only lay these out.
 */
import type { ApiShift } from '@/api/data';
import type { CostDay } from '@/api/labour';
import { T } from '@/i18n';
import { plannedHoursOn } from '@/rules';
import type { NeedProfile } from '@/shell/local';
import { num } from '@/ui/format';
import { dayFigure } from './MoneyRow';
import { type SalesDay, needHours } from './staffing';
import type { TMode } from './timeMode';

export const kr = (n: number) => Math.round(n).toLocaleString('nb-NO');
export const p1 = (n: number) => `${num(Math.round(n * 10) / 10)} %`;
export const h1 = (n: number) => num(Math.round(n * 10) / 10);
/** the target as the company set it, «25 %», not «25,0 %» */
export const pt = (n: number) => `${num(n)} %`;

/** hours are orange more than 5 % off the need, under or over */
export const hoursOff = (h: number, need: number | null) =>
  need != null && need > 0 && Math.abs(h - need) / need > 0.05;

/** over, on or under the target: over from one point above, under from one point below */
export function verdict(cost: number, sales: number, target: number): string {
  const p = (cost / sales) * 100;
  const by = cost - (sales * target) / 100;
  return p > target + 1
    ? T('{kr} kr over', { kr: kr(by) })
    : p < target - 1
      ? T('{kr} kr under', { kr: kr(-by) })
      : T('på målet');
}

/** one day as every section counts it: the hours on the plan (or the clock), its need, its cost and sales */
export function dayNumbers(
  d: string,
  o: {
    today: string;
    tmode: TMode | undefined;
    need: NeedProfile;
    needOn: boolean;
    shifts: ApiShift[];
    cost: Map<string, CostDay>;
    sales: Record<string, SalesDay>;
  },
) {
  const past = d < o.today;
  const c = o.cost.get(d);
  const s = o.sales[d];
  const f = dayFigure(c, s, past, o.tmode);
  const hours = o.shifts.filter((x) => x.user_id != null).reduce((a, x) => a + plannedHoursOn(x, d), 0);
  const nh = o.needOn ? needHours(o.need, d) : null;
  const est = !past && s?.forecast == null && s?.estimate != null;
  const side = c ? (f.worked ? c.actual : c.planned) : undefined;
  return { d, past, c, s, f, hours, nh, est, parts: side?.parts, unpriced: c?.unpriced ?? 0 };
}
export type DayNumbers = ReturnType<typeof dayNumbers>;

/** days added up: the share only over the days with sales (a closed day, 0, is left out), and how many those are */
export function sumDays(xs: DayNumbers[]) {
  const w = { k: 0, h: 0, n: 0, c: 0, s: 0, pc: 0, e: false, sd: 0, unpriced: 0 };
  for (const x of xs) {
    w.k++;
    w.h += x.hours;
    w.n += x.nh ?? 0;
    w.c += x.f.cost;
    w.unpriced += x.unpriced;
    if (x.f.sales) {
      w.s += x.f.sales;
      w.pc += x.f.cost;
      w.sd++;
      if (!x.past) w.e = true;
    }
  }
  return { ...w, pct: w.s ? (w.pc / w.s) * 100 : null };
}

/** what is missing, where it is filled in, and whether «vi bruker ikke dette» may hide it */
export type Miss = { k: string; t: string; how: string; href: string };
export function missing(o: {
  needSet: boolean;
  money: boolean;
  anySales: boolean;
  targetSet: boolean;
  unpriced: number;
  notUsed: string[];
}): Miss[] {
  const out: Miss[] = [];
  if (!o.needSet)
    out.push({
      k: 'need',
      t: T('Bemanningsbehov'),
      how: T('Hvor mange dere trenger per time og ukedag'),
      href: '/innstillinger/bemanning',
    });
  if (o.money && !o.anySales)
    out.push({ k: 'sales', t: T('Omsetning'), how: T('Skriv omsetningen uten mva i uka'), href: '#wf-oms' });
  if (o.money && !o.targetSet)
    out.push({
      k: 'target',
      t: T('Lønnsmål'),
      how: T('Hvor stor del av omsetningen lønn skal være, f.eks. 30 %'),
      href: '/omsetning',
    });
  if (o.money && o.unpriced)
    out.push({
      k: 'wages',
      t: T('Lønnssats'),
      how: T('{n} vakter uten timelønn, lønnskosten blir for lav', { n: o.unpriced }),
      href: '/ansatte',
    });
  return out.filter((m) => !o.notUsed.includes(m.k));
}
